import { defineStore } from "pinia";

/**
 * Global Store
 * Manages global application state including WMID, SID, environment, and language
 */
export const useGlobalStore = defineStore("global", {
    state: () => ({
        // Webmaster ID
        wmid: 0,

        // Session ID (for authenticated requests)
        sid: undefined,

        // Environment (uat, app, etc.)
        env: "uat",

        // Tool hash for tracking
        toolHash: {},

        // Current language
        lang: "en",

        // Performance metrics instance
        metrics: null,

        // Product name (for custom functionality per product)
        productName: "",

        // Global default selections for portfolio tools
        defaultPortfolioId: null,
        defaultTradeAccountId: null,
    }),

    getters: {
        // Check if user has valid session
        hasSession: state => state.sid !== undefined && state.sid !== null,

        // Get full API base URL
        apiBaseUrl: state => `https://${state.env}.quotemedia.com`,
    },

    actions: {
        /**
         * Set the webmaster ID
         */
        setWmid(wmid) {
            console.log("[GlobalStore] Setting WMID:", wmid);
            this.wmid = wmid;
        },

        /**
         * Set the session ID
         */
        setSid(sid) {
            console.log("[GlobalStore] Setting SID:", sid);
            this.sid = sid;
        },

        /**
         * Set the environment
         */
        setEnv(env) {
            console.log("[GlobalStore] Setting environment:", env);
            this.env = env;
        },

        /**
         * Set the language
         */
        setLang(lang) {
            console.log("[GlobalStore] Setting language:", lang);
            this.lang = lang;
        },

        /**
         * Clear session (logout)
         */
        clearSession() {
            console.log("[GlobalStore] Clearing session");
            this.sid = undefined;
        },

        /**
         * Initialize global store with values
         */
        initialize({ wmid, env, lang, sid, metrics }) {
            console.log("[GlobalStore] Initializing with:", { wmid, env, lang, sid: !!sid });

            if (wmid !== undefined) this.wmid = wmid;
            if (env !== undefined) this.env = env;
            if (lang !== undefined) this.lang = lang;
            if (sid !== undefined) this.sid = sid;
            if (metrics !== undefined) this.metrics = metrics;
        },

        setDefaultSelection({ portfolioId = null, tradeAccountId = null } = {}) {
            this.defaultPortfolioId = portfolioId;
            this.defaultTradeAccountId = tradeAccountId;
        },
    },
});

// Legacy compatibility - export reactive object that wraps the Pinia store
// This allows existing code using `store.sid` to continue working
import { computed } from "vue";

let legacyStoreInstance = null;

export const store = {
    get wmid() {
        if (!legacyStoreInstance) {
            // Use try-catch in case Pinia isn't initialized yet
            try {
                legacyStoreInstance = useGlobalStore();
            } catch (e) {
                return 0;
            }
        }
        return legacyStoreInstance.wmid;
    },
    set wmid(value) {
        if (!legacyStoreInstance) {
            try {
                legacyStoreInstance = useGlobalStore();
            } catch (e) {
                return;
            }
        }
        legacyStoreInstance.wmid = value;
    },

    get sid() {
        if (!legacyStoreInstance) {
            try {
                legacyStoreInstance = useGlobalStore();
            } catch (e) {
                return undefined;
            }
        }
        return legacyStoreInstance.sid;
    },
    set sid(value) {
        if (!legacyStoreInstance) {
            try {
                legacyStoreInstance = useGlobalStore();
            } catch (e) {
                return;
            }
        }
        legacyStoreInstance.sid = value;
    },

    get env() {
        if (!legacyStoreInstance) {
            try {
                legacyStoreInstance = useGlobalStore();
            } catch (e) {
                return "uat";
            }
        }
        return legacyStoreInstance.env;
    },
    set env(value) {
        if (!legacyStoreInstance) {
            try {
                legacyStoreInstance = useGlobalStore();
            } catch (e) {
                return;
            }
        }
        legacyStoreInstance.env = value;
    },

    get toolHash() {
        if (!legacyStoreInstance) {
            try {
                legacyStoreInstance = useGlobalStore();
            } catch (e) {
                return {};
            }
        }
        return legacyStoreInstance.toolHash;
    },
    set toolHash(value) {
        if (!legacyStoreInstance) {
            try {
                legacyStoreInstance = useGlobalStore();
            } catch (e) {
                return;
            }
        }
        legacyStoreInstance.toolHash = value;
    },

    get lang() {
        if (!legacyStoreInstance) {
            try {
                legacyStoreInstance = useGlobalStore();
            } catch (e) {
                return "en";
            }
        }
        return legacyStoreInstance.lang;
    },
    set lang(value) {
        if (!legacyStoreInstance) {
            try {
                legacyStoreInstance = useGlobalStore();
            } catch (e) {
                return;
            }
        }
        legacyStoreInstance.lang = value;
    },

    get metrics() {
        if (!legacyStoreInstance) {
            try {
                legacyStoreInstance = useGlobalStore();
            } catch (e) {
                return null;
            }
        }
        return legacyStoreInstance.metrics;
    },
    set metrics(value) {
        if (!legacyStoreInstance) {
            try {
                legacyStoreInstance = useGlobalStore();
            } catch (e) {
                return;
            }
        }
        legacyStoreInstance.metrics = value;
    },

    get defaultPortfolioId() {
        if (!legacyStoreInstance) {
            try {
                legacyStoreInstance = useGlobalStore();
            } catch (e) {
                return null;
            }
        }
        return legacyStoreInstance.defaultPortfolioId;
    },
    set defaultPortfolioId(value) {
        if (!legacyStoreInstance) {
            try {
                legacyStoreInstance = useGlobalStore();
            } catch (e) {
                return;
            }
        }
        legacyStoreInstance.defaultPortfolioId = value;
    },

    get defaultTradeAccountId() {
        if (!legacyStoreInstance) {
            try {
                legacyStoreInstance = useGlobalStore();
            } catch (e) {
                return null;
            }
        }
        return legacyStoreInstance.defaultTradeAccountId;
    },
    set defaultTradeAccountId(value) {
        if (!legacyStoreInstance) {
            try {
                legacyStoreInstance = useGlobalStore();
            } catch (e) {
                return;
            }
        }
        legacyStoreInstance.defaultTradeAccountId = value;
    },
};
